import { useMemo, useState } from 'react';
import {
  Card, CardContent, Grid, Typography, Box, Stack, Chip, LinearProgress, Divider, Table, TableBody, TableRow, TableCell,
} from '@mui/material';
import {
  ShoppingCart, LocalShipping, EngineeringOutlined, AccountBalanceWallet, Storefront, TrackChanges, WarningAmber, Inventory2, Groups,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button } from '@mui/material';
import { QueryStats } from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import { useVertical } from '../../hooks/useVertical';
import { PageHeader } from '../../components/PageHeader';
import { StatCard } from '../../components/StatCard';
import { ListFilters, EMPTY_FILTERS, toDashboardParams, type FilterValue } from '../../components/ListFilters';
import { pdiDashboardApi } from '../../api/pdiDashboard.api';
import { verticalsApi } from '../../api/verticals.api';

const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

function SummaryCard({ title, icon, rows }: { title: string; icon: React.ReactNode; rows: [string, React.ReactNode][] }) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}><CardContent>
      <Typography variant="subtitle1" gutterBottom>{icon}<Box component="span" sx={{ ml: 0.5, verticalAlign: 'middle' }}>{title}</Box></Typography>
      <Divider sx={{ mb: 1 }} />
      <Table size="small"><TableBody>
        {rows.map(([k, v]) => (
          <TableRow key={k}><TableCell sx={{ border: 0, py: 0.4, color: 'text.secondary' }}>{k}</TableCell><TableCell align="right" sx={{ border: 0, py: 0.4, fontWeight: 600 }}>{v}</TableCell></TableRow>
        ))}
      </TableBody></Table>
    </CardContent></Card>
  );
}

/**
 * HOME → unified PDI business dashboard: order, installation, finance, dealer
 * summaries + SLA + headline KPIs (vertical/region-scoped on the server).
 */
export function PdiHomePage() {
  const { user } = useAuth();
  const { verticals } = useVertical();
  const navigate = useNavigate();
  const pdiVid = verticals.find((v) => v.code === 'protection_device_installation')?.id;
  const [filters, setFilters] = useState<FilterValue>({ ...EMPTY_FILTERS });
  const fp = toDashboardParams(filters);
  const { data, isLoading } = useQuery({ queryKey: ['pdi-dashboard', fp], queryFn: () => pdiDashboardApi.overview(fp) });
  const team = useQuery({ queryKey: ['team-performance', pdiVid, 'home'], queryFn: () => verticalsApi.teamPerformance({ verticalId: pdiVid }), enabled: Boolean(pdiVid) });

  const maxTrend = useMemo(() => Math.max(1, ...(data?.orders.monthlyTrend ?? []).map((t) => t.count)), [data]);
  const firstName = (user?.fullName ?? '').split(' ')[0] || 'there';

  return (
    <>
      <PageHeader title={`Welcome back, ${firstName}`} subtitle="Protection Device Installation — your business at a glance: orders, fulfilment, finance, installations, dealers & team."
        action={<Button variant="outlined" startIcon={<QueryStats />} onClick={() => navigate('/pdi/analytics')}>Deep Analytics</Button>} />
      <ListFilters value={filters} onChange={setFilters}
        statusOptions={['draft', 'pending_approval', 'approved', 'in_manufacturing', 'manufactured', 'allocated', 'dispatched', 'delivered', 'rejected', 'cancelled']}
        show={['status', 'month', 'dateFrom', 'dateTo', 'region', 'dealer']} />
      {isLoading && <LinearProgress />}
      {data && (
        <>
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid item xs={12} sm={6} md={3}><StatCard gradient color="#1e40af" icon={<ShoppingCart />} label="Total Orders" value={data.kpis.totalOrders} hint={`${data.kpis.ordersDelivered} delivered`} /></Grid>
            <Grid item xs={12} sm={6} md={3}><StatCard gradient color="#0e7490" icon={<AccountBalanceWallet />} label="Order Value" value={inr(data.kpis.orderValue)} /></Grid>
            <Grid item xs={12} sm={6} md={3}><StatCard gradient color="#d97706" icon={<LocalShipping />} label="In Fulfilment" value={data.kpis.ordersInFulfilment} hint="awaiting dispatch/delivery" /></Grid>
            <Grid item xs={12} sm={6} md={3}><StatCard gradient color={data.kpis.installationSla >= 90 ? '#16a34a' : '#d97706'} icon={<TrackChanges />} label="Installation SLA" value={`${data.kpis.installationSla}%`} hint={`${data.kpis.installationsOpen} open`} /></Grid>
            <Grid item xs={12} sm={6} md={3}><StatCard color="#0369a1" icon={<AccountBalanceWallet />} label="Outstanding" value={inr(data.kpis.outstandingReceivable)} /></Grid>
            <Grid item xs={12} sm={6} md={3}><StatCard color="#dc2626" icon={<WarningAmber />} label="Overdue" value={inr(data.kpis.overdueReceivable)} hint="follow up on collections" /></Grid>
            <Grid item xs={12} sm={6} md={3}><StatCard color="#7c3aed" icon={<EngineeringOutlined />} label="Installations Open" value={data.kpis.installationsOpen} /></Grid>
            <Grid item xs={12} sm={6} md={3}><StatCard color="#334155" icon={<Storefront />} label="Active Dealers" value={`${data.kpis.activeDealers}/${data.kpis.totalDealers}`} hint="active / total" /></Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6} lg={3}>
              <SummaryCard title="Orders" icon={<ShoppingCart fontSize="small" sx={{ verticalAlign: 'middle' }} />} rows={[
                ['Total', data.orders.totalOrders], ['Pending Approval', data.orders.pendingApproval],
                ['In Fulfilment', data.orders.inFulfilment], ['Delivered', data.orders.delivered], ['Open', data.orders.open],
              ]} />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <SummaryCard title="Installations" icon={<EngineeringOutlined fontSize="small" sx={{ verticalAlign: 'middle' }} />} rows={[
                ['Pending', data.installations.pending], ['Ongoing', data.installations.ongoing],
                ['Material Ready', data.installations.materialReady], ['Completed', data.installations.completed],
                ['SLA Breached', <Chip key="sb" size="small" color={data.installations.slaBreached ? 'error' : 'success'} label={data.installations.slaBreached} />],
              ]} />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <SummaryCard title="Finance" icon={<AccountBalanceWallet fontSize="small" sx={{ verticalAlign: 'middle' }} />} rows={[
                ['Invoiced', inr(data.finance.totalInvoiceValue)], ['Paid', inr(data.finance.paidAmount)],
                ['Outstanding', inr(data.finance.outstandingAmount)], ['Overdue', inr(data.finance.overdueAmount)],
              ]} />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <SummaryCard title="Dealers" icon={<Storefront fontSize="small" sx={{ verticalAlign: 'middle' }} />} rows={[
                ['Total', data.dealers.total], ['Active', data.dealers.active],
                ['Pending Approval', data.dealers.pendingApproval], ['Credit Exposure', inr(data.dealers.creditExposure)],
              ]} />
            </Grid>
            {team.data && (
              <Grid item xs={12} md={6} lg={3}>
                <SummaryCard title="Team" icon={<Groups fontSize="small" sx={{ verticalAlign: 'middle' }} />} rows={[
                  ['Team Size', team.data.totals.teamSize], ['Target', inr(team.data.totals.target)],
                  ['Achieved', inr(team.data.totals.achievedRevenue)],
                  ['Achievement', <Chip key="ap" size="small" color={team.data.totals.achievementPct >= 100 ? 'success' : team.data.totals.achievementPct >= 60 ? 'warning' : 'default'} label={`${team.data.totals.achievementPct}%`} />],
                ]} />
              </Grid>
            )}
          </Grid>

          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} md={7}>
              <Card variant="outlined"><CardContent>
                <Typography variant="subtitle1" gutterBottom><ShoppingCart fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />Order Trend (last months)</Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Stack spacing={0.75}>
                  {(data.orders.monthlyTrend ?? []).map((t) => (
                    <Box key={t.month} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" sx={{ width: 64, flexShrink: 0, color: 'text.secondary' }}>{t.month}</Typography>
                      <Box sx={{ flexGrow: 1, bgcolor: 'action.hover', borderRadius: 1, height: 16 }}>
                        <Box sx={{ width: `${Math.round((t.count / maxTrend) * 100)}%`, bgcolor: 'primary.main', height: '100%', borderRadius: 1, minWidth: t.count ? 4 : 0 }} />
                      </Box>
                      <Typography variant="caption" sx={{ width: 90, textAlign: 'right' }}>{t.count} · {inr(t.value)}</Typography>
                    </Box>
                  ))}
                </Stack>
              </CardContent></Card>
            </Grid>
            <Grid item xs={12} md={5}>
              <Card variant="outlined"><CardContent>
                <Typography variant="subtitle1" gutterBottom><Inventory2 fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />Receivables Ageing</Typography>
                <Divider sx={{ mb: 1 }} />
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {(data.finance.ageing ?? []).map((a) => <Chip key={a.label} variant="outlined" label={`${a.label}: ${inr(a.value)}`} />)}
                </Stack>
              </CardContent></Card>
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
}
